import React, { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { PhoneInputField } from './PhoneNumberInput';
import { API_URL } from '../config';
import EventContext from '../contexts/EventContext';
import BalloonCanvas from './BalloonCanvas';
import Layout from './Layout';
import { useTranslation } from 'react-i18next';
import HTMLReactParser from 'html-react-parser';
import { capitalize } from '../utils';

const UPLOAD_API_URL = 'https://manager.event2one.com/videos/upload-to-youtube/upload_api.php';
const DO_PHP_URL = 'https://www.mlg-consulting.com/websites/g_event_beta/views/exhibitor_registration/do.php';
const MEDIA_BASE_URL = 'https://www.mlg-consulting.com/manager_cc/docs/archives/';
const UPLOAD_API_KEY = 'mgv_yt_upload_2026';
const LINKEDIN_CLIENT_ID = '78eqa2rddcgy4s';
const LINKEDIN_REDIRECT_URI = window.location.origin + '/linkedin-callback';
const LINKEDIN_SCOPE = 'openid profile email';


const DEFAULT_ROLE_ID = 143;

// Texte d'introduction affiché en tête du formulaire selon le coordinateur (?auteur=<id_contact>),
// par langue i18n (repli en, puis fr), signé avec la photo et l'identité du coordinateur (chargées via getContact).
const AUTEUR_INTRO_SIGNATURE_LABEL = { fr: 'Coordination', de: 'Koordination', en: 'Coordination' };
const AUTEUR_INTROS = {
    221261: {
        fr: ` 
        <p class="font-semibold text-base md:text-lg text-gray-900 dark:text-white">Appel à candidatures : Scale-ups Tech for City (Régions DACH, Nordiques et Baltes)</p>
        <p>Donnez une dimension internationale à vos innovations urbaines durables ! Le collectif myGlobalVillage ouvre gratuitement les candidatures à son challenge exclusif dédié aux scale-ups de la Tech 4 City.</p>
        <p>À la clé pour les lauréats sélectionnés :</p>
        <ul class="list-disc pl-5 space-y-1">
            <li><strong>Salon des Maires et des Collectivités Locales (24 – 26 novembre 2026, Paris)</strong> : pitchez sur scène ou à distance devant l'ensemble des décideurs publics et élus territoriaux.</li>
            <li><strong>CES Las Vegas (6 – 9 janvier 2027)</strong> : présentez votre solution en présentiel ou en ligne au cœur du plus grand événement tech mondial.</li>
            <li><strong>Nexus Luxembourg (9 – 10 mars 2027)</strong> : bénéficiez d'un stand d'exposition offert, pitchez directement devant notre cercle d'investisseurs qualifiés et tentez de remporter le grand prix de 120 000 €.</li>
        </ul>
        <p><strong>Éligibilité</strong> : ce challenge s'adresse exclusivement aux entreprises issues des pays germanophones (Allemagne, Autriche, Suisse), nordiques (Danemark, Finlande, Islande, Norvège, Suède) et baltes (Estonie, Lettonie, Lituanie).</p>
        <p><strong>Inscription 100 % gratuite.</strong></p>
    `,
        de: `
        <p class="font-semibold text-base md:text-lg text-gray-900 dark:text-white">Aufruf zur Bewerbung: „Scale-ups Tech for City“ (DACH-Region, nordische und baltische Länder)</p>
        <p>Verleihen Sie Ihren nachhaltigen städtischen Innovationen eine internationale Dimension! Das Kollektiv myGlobalVillage eröffnet kostenlos die Bewerbungsphase für seinen exklusiven Wettbewerb, der sich an Scale-ups aus dem Bereich „Tech 4 City“ richtet.</p>
        <p>Auf die ausgewählten Gewinner warten folgende Preise:</p>
        <ul class="list-disc pl-5 space-y-1">
            <li><strong>Salon des Maires et des Collectivités Locales (24.–26. November 2026, Paris)</strong>: Präsentieren Sie Ihr Projekt auf der Bühne oder per Videokonferenz vor allen öffentlichen Entscheidungsträgern und gewählten Vertretern der Kommunen.</li>
            <li><strong>CES Las Vegas (6.–9. Januar 2027)</strong>: Präsentieren Sie Ihre Lösung vor Ort oder online im Rahmen der weltweit größten Tech-Veranstaltung.</li>
            <li><strong>Nexus Luxembourg (9.–10. März 2027)</strong>: Profitieren Sie von einem kostenlosen Messestand, präsentieren Sie Ihr Projekt direkt vor unserem Kreis qualifizierter Investoren und versuchen Sie, den Hauptpreis in Höhe von 120.000 € zu gewinnen.</li>
        </ul>
        <p><strong>Teilnahmebedingungen</strong>: Dieser Wettbewerb richtet sich ausschließlich an Unternehmen aus dem deutschsprachigen Raum (Deutschland, Österreich, Schweiz), den nordischen Ländern (Dänemark, Finnland, Island, Norwegen, Schweden) und den baltischen Staaten (Estland, Lettland, Litauen).</p>
        <p><strong>Die Anmeldung ist zu 100 % kostenlos.</strong></p>
    `,
        en: `
        <p class="font-semibold text-base md:text-lg text-gray-900 dark:text-white">Call for applications: Tech for City Scale-ups (DACH, Nordic and Baltic regions)</p>
        <p>Give your sustainable urban innovations an international dimension! The myGlobalVillage collective is opening applications, free of charge, for its exclusive challenge dedicated to Tech 4 City scale-ups.</p>
        <p>What the selected winners will receive:</p>
        <ul class="list-disc pl-5 space-y-1">
            <li><strong>Salon des Maires et des Collectivités Locales (November 24–26, 2026, Paris)</strong>: pitch on stage or remotely in front of public decision-makers and local elected officials.</li>
            <li><strong>CES Las Vegas (January 6–9, 2027)</strong>: present your solution in person or online at the heart of the world's largest tech event.</li>
            <li><strong>Nexus Luxembourg (March 9–10, 2027)</strong>: get a free exhibition stand, pitch directly to our circle of qualified investors and compete for the €120,000 grand prize.</li>
        </ul>
        <p><strong>Eligibility</strong>: this challenge is open exclusively to companies from German-speaking countries (Germany, Austria, Switzerland), the Nordic countries (Denmark, Finland, Iceland, Norway, Sweden) and the Baltic states (Estonia, Latvia, Lithuania).</p>
        <p><strong>Registration is 100% free.</strong></p>
    `,
    },
};

const getYouTubeId = (url) => {
    if (!url) return null;
    for (const p of [/[?&]v=([^&]+)/, /youtu\.be\/([^?&]+)/, /youtube\.com\/embed\/([^?&]+)/]) {
        const m = url.match(p);
        if (m) return m[1];
    }
    return null;
};

// Les libellés traduits contiennent le numéro en dur ("Option N°1 :", "Option # 1:", "Opzione 1:")
// → on le remplace pour refléter l'ordre d'affichage.
const renumberOption = (label = '', n) => label.replace(/([#°]\s*|\s)\d(\s*:)/, `$1${n}$2`);

// Champs pré-remplissables depuis LinkedIn
const LI_FILLABLE = new Set(['prenom', 'nom', 'mail', 'sn_linkedin', 'fonction', 'societe']);

// Événements où le bouton "Connexion LinkedIn" est activé (désactivé par défaut)
const LINKEDIN_CONNECT_EVENTS = new Set([
    // ex: 1234, 5678
]);

const LinkedInIcon = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white shrink-0">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
);

function SelfRegisterSlot() {
    const { idEvent } = useParams();
    const { search } = useLocation();
    const { currentEvent } = useContext(EventContext);
    const { t, i18n } = useTranslation();

    const ROLE_CONFIG = useMemo(() => ({
        143: {
            pageTitle: t('form.short_form_page_title_default'),
            label: t('form.roles.participant'),
            confirmTitle: t('form.confirm.title_participant'),
            confirmDesc: t('form.confirm.desc_participant'),
            showDemo: true,
        },
        259: {
            pageTitle: t('form.short_form_page_title_default'),
            label: "Tech'Xplorateur",
            confirmTitle: t('form.confirm.title_xplorateur'),
            confirmDesc: t('form.confirm.desc_xplorateur'),
            showDemo: false,
        },
        92: {
            pageTitle: t('form.short_form_page_title_jury'),
            label: t('form.roles.jury'),
            confirmTitle: t('form.confirm.title_jury'),
            confirmDesc: t('form.confirm.desc_jury'),
            showDemo: false,
            thematiquesTitle: t('form.thematiques.title_jury'),
            thematiquesDesc: t('form.thematiques.desc_jury'),
        },
        498: {
            pageTitle: t('form.short_form_page_title_default'),
            label: 'Décideur',
            confirmTitle: t('form.confirm.title_participant'),
            confirmDesc: t('form.confirm.desc_participant'),
            showDemo: false,
            hideVisuels: true,
            thematiquesTitle: t('form.thematiques.title_jury'),
            thematiquesDesc: t('form.thematiques.desc_jury'),
        },
    }), [t]);

    const FIELDS = useMemo(() => [
        { key: 'prenom', label: capitalize(t('form.sections.contact_section.prenom')), required: true, type: 'text' },
        { key: 'nom', label: capitalize(t('form.sections.contact_section.nom')), required: true, type: 'text' },
        { key: 'societe', label: t('form.sections.organisme_section.societe'), required: true, type: 'text' },
        { key: 'fonction', label: capitalize(t('form.sections.contact_section.fonction')), required: false, type: 'text' },
        { key: 'mail', label: 'Email', required: true, type: 'email' },
        { key: 'port', label: 'Mobile', required: false, type: 'tel' },
        { key: 'sn_linkedin', label: 'Profil LinkedIn', required: false, type: 'url', placeholder: 'https://www.linkedin.com/in/...' },
        { key: 'punchline', label: <><strong>Titre de votre interview</strong> (baseline ou punchline) qui apparaîtra à l'écran (70 caractères maxi)</>, required: false, type: 'text', maxLength: 70, demoOnly: true },
        { key: 'video_url', label: "Je télécharge une vidéo de ma solution/innovation/produit qui sera diffusée en arrière-plan lors de l'enregistrement de mon webcast. (recommandé) :", required: false, type: 'url', placeholder: 'https://…', demoOnly: true },
    ], [t]);

    const urlParams = new URLSearchParams(search);

    // ?hidden_fields=fonction,linkedin,visuels → masque ces blocs (rien de masqué si absent).
    // Clés : prenom, nom, societe, fonction, mail, port (alias tel), sn_linkedin (alias linkedin),
    // visuels (photo + logo), thematiques, punchline, video (upload / YouTube), visuel_fixe.
    const FIELD_ALIASES = { tel: 'port', mobile: 'port', email: 'mail', linkedin: 'sn_linkedin' };
    const hiddenFields = new Set((urlParams.get('hidden_fields') || '')
        .split(',').map(k => k.trim().toLowerCase()).filter(Boolean).map(k => FIELD_ALIASES[k] || k));
    const isVisible = (key) => !hiddenFields.has(key);

    const DEMO_FIELDS = useMemo(() => FIELDS.filter(f => f.demoOnly && f.key === 'punchline'), [FIELDS]);
    const fields = FIELDS.filter(f => !f.demoOnly && isVisible(f.key));

    const roleId = parseInt(urlParams.get('id_event_contact_type') || DEFAULT_ROLE_ID, 10);
    const existingConfEventId = urlParams.get('id_conf_event') ? parseInt(urlParams.get('id_conf_event'), 10) : null;
    const existingContactId = urlParams.get('id_contact') ? parseInt(urlParams.get('id_contact'), 10) : null;
    const existingPrestaId = urlParams.get('id_presta') ? parseInt(urlParams.get('id_presta'), 10) : null;
    const existingConferencierId = urlParams.get('id_conferencier') ? parseInt(urlParams.get('id_conferencier'), 10) : null;
    const existingVideoHosted = urlParams.get('video_hosted') || '';
    const existingPrestaVisuel = urlParams.get('presta_visuel') || '';
    // ?auteur=<id_contact> (alias ?coordinateur=) → coordinateur rattaché au contact créé (contacts.auteur)
    const auteur = urlParams.get('auteur') || urlParams.get('coordinateur') || '';
    const roleConfig = ROLE_CONFIG[roleId] ?? ROLE_CONFIG[DEFAULT_ROLE_ID];
    const showLinkedInConnect = LINKEDIN_CONNECT_EVENTS.has(parseInt(idEvent, 10));

    const FIELD_KEYS = useMemo(() => FIELDS.map(f => f.key), [FIELDS]);
    const [form, setForm] = useState(() => Object.fromEntries(FIELD_KEYS.map(k => [k, ''])));
    const [status, setStatus] = useState('idle'); // idle | submitting | done | error
    const [errorMsg, setErrorMsg] = useState('');
    const [liProfile, setLiProfile] = useState(null);   // { prenom, nom, mail, sn_linkedin, fonction, photo }
    const [liLoading, setLiLoading] = useState(false);
    const [liError, setLiError] = useState('');
    const popupRef = useRef(null);

    const [videoHostedFile, setVideoHostedFile] = useState(null);
    const [videoHostedPreviewUrl, setVideoHostedPreviewUrl] = useState('');
    const [prestaVisuelFile, setPrestaVisuelFile] = useState(null);
    const [prestaVisuelPreview, setPrestaVisuelPreview] = useState(existingPrestaVisuel);
    const [fetchedVideoHosted, setFetchedVideoHosted] = useState(existingVideoHosted);

    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');

    // ─── Thématiques (plans d'action) ────────────────────────────────────────
    // Plans d'action rattachés à la ligne d'événement s'il y en a, sinon liste
    // des plans d'action actifs. Choix multiple, enregistré dans contact_cycle_lang.
    const [thematiqueOptions, setThematiqueOptions] = useState([]);
    const [selectedThematiques, setSelectedThematiques] = useState([]);
    const [initialThematiques, setInitialThematiques] = useState([]);
    // Collectifs programmes actifs de chaque plan d'action lié à la ligne d'événement
    const [programmesByPlan, setProgrammesByPlan] = useState({});

    const idEventLigneCurrent = currentEvent?.event_ligne?.id_event_ligne;

    useEffect(() => {
        if (!currentEvent) return;
        const loadDefault = () => fetch(`${API_URL}?action=getCycleLang&filter=cycle_lang_type IN('collectif_planaction') AND cycle_lang_state='active' AND &get_parents=0&get_children=0`)
            .then(r => r.json())
            .then(data => Array.isArray(data) ? data : []);

        const loadEventLigne = () => idEventLigneCurrent
            ? fetch(`${API_URL}?action=getCycleLangEventLigne&params=${encodeURIComponent(`WHERE id_event_ligne=${idEventLigneCurrent}`)}`)
                .then(r => r.json())
                .then(data => (Array.isArray(data) ? data : [])
                    .map(cle => cle.id_cycle_lang)
                    .filter(cl => cl?.id_cycle_lang && cl.cycle_lang_type === 'collectif_planaction' && cl.cycle_lang_state !== 'hidden'))
            : Promise.resolve([]);

        // Programmes actifs (enfants collectif_programme) des plans d'action liés
        const loadProgrammes = (plans) => {
            const ids = plans.map(cl => Number(cl.id_cycle_lang)).filter(id => Number.isInteger(id) && id > 0);
            if (ids.length === 0) return Promise.resolve({});
            return fetch(`${API_URL}?action=getCycleLang&filter=id_cycle_lang IN (${ids.join(',')}) AND &get_parents=0&get_children=1`)
                .then(r => r.json())
                .then(data => Object.fromEntries((Array.isArray(data) ? data : []).map(plan => {
                    const seen = new Set();
                    return [String(plan.id_cycle_lang), (plan.children || [])
                        .filter(c => c?.id_cycle_lang && c.cycle_lang_type === 'collectif_programme' && c.cycle_lang_state === 'active')
                        .filter(c => !seen.has(String(c.id_cycle_lang)) && seen.add(String(c.id_cycle_lang)))
                        .sort((a, b) => (a.nom_cycle_lang || '').localeCompare(b.nom_cycle_lang || ''))];
                })))
                .catch(() => ({}));
        };

        loadEventLigne()
            .then(async linked => {
                const list = linked.length > 0 ? linked : await loadDefault();
                const seen = new Set();
                const plans = list
                    .filter(cl => !seen.has(String(cl.id_cycle_lang)) && seen.add(String(cl.id_cycle_lang)))
                    .sort((a, b) => (a.nom_cycle_lang || '').localeCompare(b.nom_cycle_lang || ''));
                setThematiqueOptions(plans);
                setProgrammesByPlan(linked.length > 0 ? await loadProgrammes(plans) : {});
            })
            .catch(() => { setThematiqueOptions([]); setProgrammesByPlan({}); });
    }, [currentEvent, idEventLigneCurrent]);

    // Thématiques déjà associées au contact (lien personnalisé)
    useEffect(() => {
        if (!existingContactId) return;
        fetch(`${API_URL}?action=getContactCycleLang&filter= id_contact=${existingContactId}`)
            .then(r => r.json())
            .then(data => {
                const ids = (Array.isArray(data) ? data : [])
                    .map(ccl => String(ccl.id_cycle_lang?.id_cycle_lang ?? ccl.id_cycle_lang))
                    .filter(id => id && id !== '0');
                setInitialThematiques(ids);
                setSelectedThematiques(ids);
            })
            .catch(() => { });
    }, [existingContactId]);

    // Ids proposés : plans d'action + programmes rattachés
    const thematiqueOptionIds = [
        ...thematiqueOptions.map(cl => String(cl.id_cycle_lang)),
        ...Object.values(programmesByPlan).flat().map(cl => String(cl.id_cycle_lang)),
    ];

    const toggleThematique = (id) => setSelectedThematiques(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );

    // Cocher un programme coche aussi son plan d'action parent
    const toggleProgramme = (id, planId) => setSelectedThematiques(prev =>
        prev.includes(id)
            ? prev.filter(x => x !== id)
            : [...prev, id, ...(prev.includes(planId) ? [] : [planId])]
    );

    // Diff sur les seules thématiques proposées : on ne touche pas aux autres
    // collectifs auxquels le contact appartient déjà.
    const syncThematiques = async (id_contact) => {
        if (!id_contact) return;
        const optionIds = thematiqueOptionIds;
        const toAdd = selectedThematiques.filter(id => optionIds.includes(id) && !initialThematiques.includes(id));
        const toRemove = initialThematiques.filter(id => optionIds.includes(id) && !selectedThematiques.includes(id));
        await Promise.all([
            ...toAdd.map(id_cycle_lang => fetch(`${API_URL}?action=createContactCycleLang`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_contact, id_cycle_lang, id_event_contact_type: [roleId] }),
            })),
            ...toRemove.map(id_cycle_lang => fetch(`${API_URL}?action=deleteContactCycleLang&id_cycle_lang=${id_cycle_lang}&id_contact=${id_contact}`)),
        ]);
    };

    // Intro dans la langue courante (i18n), repli sur l'anglais puis le français
    const auteurIntros = AUTEUR_INTROS[auteur];
    const auteurIntro = auteurIntros ? (auteurIntros[(i18n.language || 'fr').slice(0, 2)] || auteurIntros.en || auteurIntros.fr) : null;
    const [auteurContact, setAuteurContact] = useState(null);
    useEffect(() => {
        if (!auteurIntros) return;
        fetch(`${API_URL}?action=getContact&id_contact=${auteur}`)
            .then(r => r.json())
            .then(contact => contact && setAuteurContact(contact))
            .catch(() => { });
    }, [auteur, auteurIntros]);

    useEffect(() => {
        if (!existingContactId) return;
        fetch(`${API_URL}?action=getContact&id_contact=${existingContactId}`)
            .then(r => r.json())
            .then(contact => {
                if (!contact) return;
                setForm(f => ({
                    ...f,
                    prenom: contact.prenom || '',
                    nom: contact.nom || '',
                    mail: contact.mail || '',
                    societe: contact.societe || '',
                    fonction: contact.fonction || '',
                    port: contact.port || '',
                    sn_linkedin: contact.sn_linkedin || '',
                }));
                if (contact.photos?.small) setPhotoPreview(contact.photos.small);
                if (contact.logos?.small) setLogoPreview(contact.logos.small);
            })
            .catch(() => { });
    }, [existingContactId]);

    const uploadContactVisuel = async (visuelType, id_contact, file, id_event_ligne) => {
        if (!file || !id_contact) return;
        const fd = new FormData();
        fd.append('do', 'update_visuels_candidature');
        fd.append('visuel', visuelType);
        fd.append('id_contact', id_contact);
        fd.append('Filedata', file);
        fd.append('id_event', idEvent);
        if (id_event_ligne) fd.append('id_event_ligne', id_event_ligne);
        await fetch(DO_PHP_URL, { method: 'POST', body: fd });
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0] || null;
        setPhotoFile(file);
        if (file) setPhotoPreview(URL.createObjectURL(file));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0] || null;
        setLogoFile(file);
        if (file) setLogoPreview(URL.createObjectURL(file));
    };

    // Session ciblée (id_conf_event) → titre "Votre inscription : {session ou type}" + ligne Dates
    const [confEvent, setConfEvent] = useState(null);
    useEffect(() => {
        if (!existingConfEventId || !currentEvent?.id_event) return;
        const filter = encodeURIComponent(` AND ce.id_conf_event=${existingConfEventId}`);
        fetch(`${API_URL}?action=getConfeventLight&id_event=${currentEvent.id_event}&filter=${filter}`)
            .then(r => r.json())
            .then(data => setConfEvent(Array.isArray(data) ? (data[0] || null) : null))
            .catch(() => setConfEvent(null));
    }, [existingConfEventId, currentEvent?.id_event]);

    const cleanLabel = (str = '') => str.replace(/ /g, ' ').trim();
    const confEventTypeName = cleanLabel(confEvent?.type?.conf_event_type_nom);
    const confEventLabel = cleanLabel(confEvent?.cel_titre) || confEventTypeName;
    const isWebcast = /webcast/i.test(confEventTypeName);
    const pageTitle = confEventLabel && !isWebcast
        ? `Votre inscription à ${capitalize(confEventLabel)}`
        : `${roleConfig.pageTitle || 'Enregistrement de votre webcast'} — ${currentEvent?.nom || ''}`;

    const confEventDates = useMemo(() => {
        const date = confEvent?.conf_event_date_local || confEvent?.conf_event_date;
        if (!date) return '';
        const day = new Date(`${date}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        const start = confEvent.heure_debut_local || confEvent.heure_debut;
        const end = confEvent.heure_fin_local || confEvent.heure_fin;
        const hours = start ? ` · ${start}${end ? ` – ${end}` : ''}` : '';
        return `${capitalize(day)}${hours}`;
    }, [confEvent]);

    useEffect(() => {
        if (!existingPrestaId) return;
        fetch(`${API_URL}?action=getPrestaList&params=${encodeURIComponent(`WHERE id_presta=${existingPrestaId} LIMIT 1`)}`)
            .then(r => r.json())
            .then(data => {
                if (!Array.isArray(data) || !data[0]) return;
                const p = data[0];
                if (p.video_hosted) setFetchedVideoHosted(p.video_hosted);
                const visuelSrc = p.presta_visuel_thumbs?.source || p.presta_visuel || '';
                if (visuelSrc) setPrestaVisuelPreview(visuelSrc);
                if (p.punchline && !form.punchline) setForm(f => ({ ...f, punchline: p.punchline }));
                if (p.video_url && !form.video_url) setForm(f => ({ ...f, video_url: p.video_url }));
            })
            .catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingPrestaId]);

    const handleChange = (key, value) => setForm(f => ({ ...f, [key]: value }));

    const handleVideoHostedChange = (e) => {
        const file = e.target.files[0] || null;
        setVideoHostedFile(file);
        setVideoHostedPreviewUrl(file ? URL.createObjectURL(file) : '');
    };

    const handlePrestaVisuelChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setPrestaVisuelFile(file);
        const reader = new FileReader();
        reader.onload = ev => setPrestaVisuelPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    // ─── LinkedIn OAuth ──────────────────────────────────────────────────────

    const handleLinkedInConnect = () => {
        const state = Math.random().toString(36).substring(2, 18);
        sessionStorage.setItem('linkedin_oauth_state', state);

        const params = new URLSearchParams({
            response_type: 'code',
            client_id: LINKEDIN_CLIENT_ID,
            redirect_uri: LINKEDIN_REDIRECT_URI,
            scope: LINKEDIN_SCOPE,
            state,
        });

        sessionStorage.setItem('linkedin_return_url', window.location.href);

        const popup = window.open(
            `https://www.linkedin.com/oauth/v2/authorization?${params}`,
            'linkedin_auth',
            'width=600,height=700,top=100,left=300'
        );
        popupRef.current = popup;
        setLiLoading(true);
        setLiError('');
    };

    useEffect(() => {
        const handleMessage = async (event) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type !== 'linkedin_callback') return;

            const { code, state, error } = event.data;

            if (error || !code) {
                setLiLoading(false);
                setLiError('Connexion LinkedIn annulée.');
                return;
            }

            if (state !== sessionStorage.getItem('linkedin_oauth_state')) {
                setLiLoading(false);
                setLiError('Erreur de sécurité OAuth (state mismatch).');
                return;
            }

            sessionStorage.removeItem('linkedin_oauth_state');

            try {
                const fd = new FormData();
                fd.append('code', code);
                fd.append('redirect_uri', LINKEDIN_REDIRECT_URI);

                const res = await fetch(`${UPLOAD_API_URL}?action=linkedin_token_exchange`, {
                    method: 'POST',
                    headers: { 'X-Api-Key': UPLOAD_API_KEY },
                    body: fd,
                });
                const data = await res.json();

                if (data.error) throw new Error(data.error + (data.detail ? ' — ' + JSON.stringify(data.detail) : ''));

                setLiProfile(data);
                setForm(f => ({
                    ...f,
                    ...(data.prenom ? { prenom: data.prenom } : {}),
                    ...(data.nom ? { nom: data.nom } : {}),
                    ...(data.mail ? { mail: data.mail } : {}),
                    ...(data.sn_linkedin ? { sn_linkedin: data.sn_linkedin } : {}),
                    ...(data.fonction ? { fonction: data.fonction } : {}),
                    ...(data.societe ? { societe: data.societe } : {}),
                }));
            } catch (e) {
                setLiError('Impossible de récupérer le profil LinkedIn : ' + e.message);
            } finally {
                setLiLoading(false);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, []);

    // Fallback : même onglet (window.opener coupé par COOP en prod HTTPS)
    useEffect(() => {
        const pending = sessionStorage.getItem('linkedin_pending');
        if (!pending) return;
        sessionStorage.removeItem('linkedin_pending');
        sessionStorage.removeItem('linkedin_return_url');

        const { code, state, error } = JSON.parse(pending);
        setLiLoading(true);

        if (error || !code) {
            setLiLoading(false);
            setLiError('Connexion LinkedIn annulée.');
            return;
        }
        if (state !== sessionStorage.getItem('linkedin_oauth_state')) {
            setLiLoading(false);
            setLiError('Erreur de sécurité OAuth (state mismatch).');
            return;
        }
        sessionStorage.removeItem('linkedin_oauth_state');

        (async () => {
            try {
                const fd = new FormData();
                fd.append('code', code);
                fd.append('redirect_uri', LINKEDIN_REDIRECT_URI);
                const res = await fetch(`${UPLOAD_API_URL}?action=linkedin_token_exchange`, {
                    method: 'POST',
                    headers: { 'X-Api-Key': UPLOAD_API_KEY },
                    body: fd,
                });
                const data = await res.json();
                if (data.error) throw new Error(data.error + (data.detail ? ' — ' + JSON.stringify(data.detail) : ''));
                setLiProfile(data);
                setForm(f => ({
                    ...f,
                    ...(data.prenom ? { prenom: data.prenom } : {}),
                    ...(data.nom ? { nom: data.nom } : {}),
                    ...(data.mail ? { mail: data.mail } : {}),
                    ...(data.sn_linkedin ? { sn_linkedin: data.sn_linkedin } : {}),
                    ...(data.fonction ? { fonction: data.fonction } : {}),
                    ...(data.societe ? { societe: data.societe } : {}),
                }));
            } catch (e) {
                setLiError('Impossible de récupérer le profil LinkedIn : ' + e.message);
            } finally {
                setLiLoading(false);
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Submit ──────────────────────────────────────────────────────────────

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('submitting');
        setErrorMsg('');
        try {
            if (existingConferencierId) {
                // ── Mode mise à jour : id_conferencier présent ──────────────
                // 1. updateContact
                if (existingContactId) {
                    await fetch(`${API_URL}?action=updateContact`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            formData: {
                                id_contact: existingContactId,
                                ...form,
                                ...(liProfile?.photo ? { photo_linkedin: liProfile.photo } : {}),
                            },
                        }),
                    });
                }

                // 2. updatePresta (champs texte)
                const prestaPayload = {};
                if (form.punchline) prestaPayload.punchline = form.punchline;
                if (form.video_url) prestaPayload.video_url = form.video_url;

                const id_presta = existingPrestaId;
                if (id_presta && Object.keys(prestaPayload).length > 0) {
                    await fetch(`${API_URL}?action=updatePresta`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ id_presta, ...prestaPayload }),
                    });
                }

                // 3. Upload fichiers
                if (id_presta) {
                    const uploadPrestaFile = async (file, file_name) => {
                        const fd = new FormData();
                        fd.append('id_presta', id_presta);
                        fd.append('file_name', file_name);
                        fd.append(file_name, file);
                        await fetch(`${API_URL}?action=updatePresta`, { method: 'POST', body: fd });
                    };
                    if (videoHostedFile) try { await uploadPrestaFile(videoHostedFile, 'video_hosted'); } catch { /* silencieux */ }
                    if (prestaVisuelFile) try { await uploadPrestaFile(prestaVisuelFile, 'presta_visuel'); } catch { /* silencieux */ }
                }

                // 4. Upload photo / logo contact
                const idEventLigne = currentEvent?.event_ligne?.id_event_ligne;
                if (photoFile) try { await uploadContactVisuel('photo', existingContactId, photoFile, idEventLigne); } catch { /* silencieux */ }
                if (logoFile) try { await uploadContactVisuel('logo', existingContactId, logoFile, idEventLigne); } catch { /* silencieux */ }

                // 5. Thématiques
                try { await syncThematiques(existingContactId); } catch { /* silencieux */ }

                setStatus('done');
                return;
            }

            // ── Mode création ────────────────────────────────────────────────

            // 1. Contact existant ou création
            let id_contact = existingContactId;
            if (!id_contact) {
                const contactRes = await fetch(`${API_URL}?action=createContact`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...form,
                        ...(liProfile?.photo ? { photo_linkedin: liProfile.photo } : {}),
                        ...(auteur ? { auteur } : {}),
                    }),
                });
                const contactRaw = await contactRes.text();
                const contactData = JSON.parse(contactRaw);
                id_contact = typeof contactData === 'string' || typeof contactData === 'number'
                    ? parseInt(contactData, 10)
                    : contactData?.id_contact;
                if (!id_contact) throw new Error('La création du contact a échoué.');
            }

            // 2. Utiliser le créneau existant ou en créer un nouveau
            let id_conf_event = existingConfEventId;
            if (!id_conf_event) {
                const ceRes = await fetch(`${API_URL}?action=createConfEvent`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id_event: idEvent }),
                });
                id_conf_event = parseInt(JSON.parse(await ceRes.text()), 10);
                if (!id_conf_event) throw new Error('La création du créneau a échoué.');
            }

            // 3. Lier le contact au créneau
            const confRes = await fetch(`${API_URL}?action=createConferencier`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_contact, id_conf_event, statut: roleId, id_event: idEvent }),
            });
            const confRaw = JSON.parse(await confRes.text());
            const id_conferencier = typeof confRaw === 'string' || typeof confRaw === 'number'
                ? parseInt(confRaw, 10)
                : confRaw?.id_conferencier;
            if (!id_conferencier) throw new Error("L'association a échoué.");

            // 4. Créer ou mettre à jour la presta (champs texte)
            const prestaPayload = {};
            if (form.punchline) prestaPayload.punchline = form.punchline;
            if (form.video_url) prestaPayload.video_url = form.video_url;

            let id_presta = existingPrestaId;
            if (id_presta) {
                await fetch(`${API_URL}?action=updatePresta`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id_presta, ...prestaPayload }),
                });
            } else {
                const pr = await fetch(`${API_URL}?action=createPresta`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id_contact, ...prestaPayload }),
                });
                const prData = await pr.json();
                id_presta = prData?.id_presta || parseInt(prData, 10) || null;
            }

            // 5. Upload fichiers via updatePresta (file_name + file en FormData)
            if (id_presta) {
                const uploadPrestaFile = async (file, file_name) => {
                    const fd = new FormData();
                    fd.append('id_presta', id_presta);
                    fd.append('file_name', file_name);
                    fd.append(file_name, file); // $_FILES[$file_name] côté PHP
                    await fetch(`${API_URL}?action=updatePresta`, { method: 'POST', body: fd });
                };
                if (videoHostedFile) try { await uploadPrestaFile(videoHostedFile, 'video_hosted'); } catch { /* silencieux */ }
                if (prestaVisuelFile) try { await uploadPrestaFile(prestaVisuelFile, 'presta_visuel'); } catch { /* silencieux */ }
            }

            // 6. Upload photo / logo contact
            const idEventLigne = currentEvent?.event_ligne?.id_event_ligne;
            if (photoFile) try { await uploadContactVisuel('photo', id_contact, photoFile, idEventLigne); } catch { /* silencieux */ }
            if (logoFile) try { await uploadContactVisuel('logo', id_contact, logoFile, idEventLigne); } catch { /* silencieux */ }

            // 7. Thématiques
            try { await syncThematiques(id_contact); } catch { /* silencieux */ }

            setStatus('done');
        } catch (err) {
            setErrorMsg(err.message);
            setStatus('error');
        }
    };

    // ─── Done screen ─────────────────────────────────────────────────────────

    if (status === 'done') {
        return (
            <Layout title="Inscription confirmée">
                <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center px-4 relative">
                    <BalloonCanvas />
                    <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-gray-100 dark:border-neutral-800 max-w-md w-full p-8 text-center space-y-4 relative" style={{ zIndex: 1 }}>
                        <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
                            <svg className="w-7 h-7 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h1 className="text-xl font-bold text-gray-800 dark:text-white">{roleConfig.confirmTitle}</h1>
                        <p className="text-gray-500 dark:text-neutral-400 text-sm">
                            Merci <strong>{form.prenom} {form.nom}</strong>. {roleConfig.confirmDesc}
                        </p>
                        {currentEvent && (
                            <p className="text-xs text-gray-400 dark:text-neutral-500 pt-2 border-t border-gray-100 dark:border-neutral-800">{currentEvent.nom}</p>
                        )}
                    </div>
                </div>
            </Layout>
        );
    }

    // ─── Form ─────────────────────────────────────────────────────────────────

    return (
        <Layout title={pageTitle}>
            <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 px-4 py-12">
                <div className="max-w-4xl mx-auto space-y-4">

                    {/* Rappel session : dates + événement */}
                    {confEvent && !isWebcast && (
                        <div className="-mt-8 mb-2 text-center space-y-1">
                            {confEventDates && (
                                <p className="text-sm md:text-base text-gray-700 dark:text-neutral-200">
                                    <span className="font-semibold">Dates :</span> {confEventDates}
                                </p>
                            )}
                            {currentEvent?.nom && (
                                <p className="text-xs md:text-sm text-gray-500 dark:text-neutral-400">Dans le cadre de {currentEvent.nom}</p>
                            )}
                        </div>
                    )}

                    {/* Introduction du coordinateur (?auteur=) + signature */}
                    {auteurIntro && (
                        <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-gray-100 dark:border-neutral-800 p-5 md:p-8 space-y-5">
                            <div className="space-y-3 text-sm md:text-base text-gray-700 dark:text-neutral-300 leading-relaxed">
                                {HTMLReactParser(auteurIntro)}
                            </div>
                            {auteurContact && (
                                <div className="flex items-center gap-4 pt-5 border-t border-gray-100 dark:border-neutral-800">
                                    {auteurContact.photos?.small && (
                                        <img src={auteurContact.photos.small} alt={`${auteurContact.prenom} ${auteurContact.nom}`}
                                            className="w-14 h-14 md:w-16 md:h-16 rounded-full object-cover border-2 border-e2o-400 shrink-0" />
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs uppercase tracking-wide text-gray-400 dark:text-neutral-500">{AUTEUR_INTRO_SIGNATURE_LABEL[(i18n.language || 'fr').slice(0, 2)] || AUTEUR_INTRO_SIGNATURE_LABEL.en}</p>
                                        <p className="font-semibold text-gray-900 dark:text-white">{capitalize(auteurContact.prenom)} {capitalize(auteurContact.nom)}</p>
                                        {auteurContact.fonction && <p className="text-sm text-gray-600 dark:text-neutral-400">{auteurContact.fonction}</p>}
                                        {auteurContact.societe && <p className="text-sm text-gray-500 dark:text-neutral-500 truncate">{auteurContact.societe}</p>}
                                    </div>
                                    {auteurContact.sn_linkedin && (
                                        <a href={auteurContact.sn_linkedin} target="_blank" rel="noopener noreferrer"
                                            className="text-sm font-medium text-e2o-600 hover:text-e2o-700 dark:text-e2o-400 shrink-0">LinkedIn</a>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* LinkedIn connect — activé par événement via LINKEDIN_CONNECT_EVENTS */}
                    {showLinkedInConnect && (!liProfile ? (
                        <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-gray-100 dark:border-neutral-800 p-6 text-center space-y-4">
                            <div className="space-y-1">
                                <p className="text-sm font-semibold text-gray-800 dark:text-white">Inscription rapide</p>
                                <p className="text-xs text-gray-500 dark:text-neutral-400">Pré-remplissez le formulaire en un clic avec votre profil LinkedIn</p>
                            </div>

                            <button
                                type="button"
                                onClick={handleLinkedInConnect}
                                disabled={liLoading}
                                className="inline-flex items-center justify-center gap-2.5 px-5 py-2.5 rounded-lg font-semibold text-sm text-white bg-[#0A66C2] hover:bg-[#004182] disabled:opacity-60 transition-colors w-full max-w-xs mx-auto"
                            >
                                {liLoading
                                    ? <><svg className="w-4 h-4 animate-spin fill-white" viewBox="0 0 24 24"><path d="M12 2a10 10 0 0 1 10 10h-2a8 8 0 0 0-8-8V2z" /></svg> Connexion…</>
                                    : <><LinkedInIcon /> Continuer avec LinkedIn</>
                                }
                            </button>

                            {liError && (
                                <p className="text-xs text-red-500">{liError}</p>
                            )}

                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-px bg-gray-200 dark:bg-neutral-700" />
                                <span className="text-xs text-gray-400 dark:text-neutral-500">ou remplir manuellement</span>
                                <div className="flex-1 h-px bg-gray-200 dark:bg-neutral-700" />
                            </div>
                        </div>
                    ) : (
                        /* Badge profil connecté */
                        <div className="flex items-center gap-3 bg-[#0A66C2]/8 dark:bg-[#0A66C2]/15 border border-[#0A66C2]/20 rounded-xl px-4 py-3">
                            {liProfile.photo
                                ? <img src={liProfile.photo} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                                : <div className="w-10 h-10 rounded-full bg-[#0A66C2] flex items-center justify-center shrink-0"><LinkedInIcon /></div>
                            }
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-800 dark:text-white truncate">{liProfile.prenom} {liProfile.nom}</p>
                                <p className="text-xs text-[#0A66C2] dark:text-blue-400">Connecté via LinkedIn · formulaire pré-rempli</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => { setLiProfile(null); setLiError(''); }}
                                className="text-xs text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300 shrink-0"
                            >
                                Déconnecter
                            </button>
                        </div>
                    ))}

                    {/* Formulaire */}
                    <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-gray-100 dark:border-neutral-800 p-6 md:p-8">
                        <p className="text-sm text-gray-600 dark:text-neutral-400 mb-6">
                            {t('form.short_form_instructions')}

                        </p>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {fields.map(({ key, label, required, type, placeholder, full }) => (
                                    <div key={key} className={full ? 'sm:col-span-2' : ''}>
                                        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-1">
                                            {label}
                                            {required && <span className="text-red-400">*</span>}
                                            {liProfile && LI_FILLABLE.has(key) && form[key] && (
                                                <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium text-[#0A66C2] dark:text-blue-400">
                                                    <LinkedInIcon />
                                                </span>
                                            )}
                                        </label>
                                        {key === 'port' ? (
                                            <PhoneInputField
                                                name="port"
                                                id="port"
                                                required={required}
                                                value={form.port}
                                                onChange={phone => handleChange('port', phone)}
                                            />
                                        ) : key === 'sn_linkedin' ? (
                                            <>
                                                <input
                                                    type={type}
                                                    required={required}
                                                    placeholder={placeholder || ''}
                                                    value={form[key]}
                                                    onChange={e => handleChange(key, e.target.value)}
                                                    className="w-full border border-gray-200 dark:border-neutral-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-e2o-400 focus:border-transparent"
                                                />
                                                {(form.prenom || form.nom) && (
                                                    <p className="mt-1 text-[11px] text-[#0A66C2] dark:text-blue-400">
                                                        <a
                                                            href={`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(`${form.prenom} ${form.nom}`.trim())}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="underline font-medium"
                                                        >
                                                            🔍 Rechercher {form.prenom} {form.nom} sur LinkedIn
                                                        </a>
                                                        {' '}— copiez l'URL de votre profil dans le champ
                                                    </p>
                                                )}
                                                {liProfile && !form.sn_linkedin && (
                                                    <p className="mt-1 text-[11px] text-[#0A66C2] dark:text-blue-400">
                                                        Retrouvez votre URL sur{' '}
                                                        <a
                                                            href="https://www.linkedin.com/in/"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="underline font-medium"
                                                        >
                                                            votre profil LinkedIn
                                                        </a>
                                                        {' '}→ copiez l'adresse de la page
                                                    </p>
                                                )}
                                            </>
                                        ) : (
                                            <input
                                                type={type}
                                                required={required}
                                                placeholder={placeholder || ''}
                                                value={form[key]}
                                                onChange={e => handleChange(key, e.target.value)}
                                                className="w-full border border-gray-200 dark:border-neutral-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-e2o-400 focus:border-transparent"
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Photo & Logo */}
                            {!roleConfig.hideVisuels && isVisible('visuels') && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                                    {[
                                        { key: 'photo', label: 'Photo de profil', preview: photoPreview, onChange: handlePhotoChange, accept: 'image/*' },
                                        { key: 'logo', label: 'Logo / visuel organisme', preview: logoPreview, onChange: handleLogoChange, accept: 'image/*' },
                                    ].map(({ key, label, preview, onChange, accept }) => (
                                        <div key={key}>
                                            <label className="block text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-1">{label}</label>
                                            <div className="flex items-center gap-3">
                                                {preview ? (
                                                    <img src={preview} alt={label} className="w-14 h-14 rounded-lg object-cover border border-gray-200 dark:border-neutral-700 shrink-0" />
                                                ) : (
                                                    <div className="w-14 h-14 rounded-lg border border-dashed border-gray-200 dark:border-neutral-700 flex items-center justify-center shrink-0">
                                                        <span className="text-[10px] text-gray-400 dark:text-neutral-500">aperçu</span>
                                                    </div>
                                                )}
                                                <input
                                                    type="file"
                                                    accept={accept}
                                                    onChange={onChange}
                                                    className="flex-1 text-sm text-gray-500 dark:text-neutral-400 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-gray-100 dark:file:bg-neutral-800 file:text-gray-700 dark:file:text-neutral-200 hover:file:bg-gray-200 dark:hover:file:bg-neutral-700 cursor-pointer"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {thematiqueOptions.length > 0 && isVisible('thematiques') && (
                                <div className="space-y-2 pt-2">
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-semibold text-e2o-700 dark:text-e2o-400 uppercase tracking-wide">{roleConfig.thematiquesTitle || t('form.thematiques.title_default')}</p>
                                        {selectedThematiques.length > 0 && (
                                            <span className="text-[11px] text-gray-400 dark:text-neutral-500">{t('form.thematiques.selected_count', { count: selectedThematiques.filter(id => thematiqueOptionIds.includes(id)).length })}</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-neutral-400">{roleConfig.thematiquesDesc || t('form.thematiques.desc_default')}</p>
                                    {Object.values(programmesByPlan).some(list => list.length > 0) ? (
                                        <div className="space-y-3 max-h-[28rem] overflow-y-auto pr-1">
                                            {thematiqueOptions.map(cl => {
                                                const id = String(cl.id_cycle_lang);
                                                const checked = selectedThematiques.includes(id);
                                                const logo = cl.attachedFilesCollection?.find(f => f.id_attached_file_type == 65)?.tiny; // eslint-disable-line eqeqeq
                                                const programmes = programmesByPlan[id] || [];
                                                return (
                                                    <div key={id} className="rounded-lg border border-gray-200 dark:border-neutral-700 overflow-hidden">
                                                        <label className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm font-medium transition-colors ${checked
                                                            ? 'bg-e2o-50 dark:bg-e2o-950/30 text-gray-800 dark:text-white'
                                                            : 'text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800'}`}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={checked}
                                                                onChange={() => toggleThematique(id)}
                                                                className="accent-e2o-500 shrink-0"
                                                            />
                                                            {logo && <img src={logo} alt="" className="w-6 h-6 rounded object-cover shrink-0" />}
                                                            <span className="min-w-0 flex-1">{cl.nom_cycle_lang}</span>
                                                            {programmes.length > 0 && (
                                                                <span className="text-[10px] text-gray-400 dark:text-neutral-500 shrink-0">{programmes.length} programme{programmes.length > 1 ? 's' : ''}</span>
                                                            )}
                                                        </label>
                                                        {programmes.length > 0 && (
                                                            <div className="border-t border-gray-100 dark:border-neutral-800 px-3 py-2 space-y-1">
                                                                {programmes.map(prog => {
                                                                    const progId = String(prog.id_cycle_lang);
                                                                    const progChecked = selectedThematiques.includes(progId);
                                                                    return (
                                                                        <label
                                                                            key={progId}
                                                                            className={`flex items-start gap-2 pl-4 pr-2 py-1 rounded-md cursor-pointer text-xs transition-colors ${progChecked
                                                                                ? 'bg-e2o-50 dark:bg-e2o-950/30 text-gray-800 dark:text-white'
                                                                                : 'text-gray-600 dark:text-neutral-400 hover:bg-gray-50 dark:hover:bg-neutral-800'}`}
                                                                        >
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={progChecked}
                                                                                onChange={() => toggleProgramme(progId, id)}
                                                                                className="accent-e2o-500 shrink-0 mt-0.5"
                                                                            />
                                                                            <span className="min-w-0">{prog.nom_cycle_lang}</span>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
                                            {thematiqueOptions.map(cl => {
                                                const id = String(cl.id_cycle_lang);
                                                const checked = selectedThematiques.includes(id);
                                                const logo = cl.attachedFilesCollection?.find(f => f.id_attached_file_type == 65)?.tiny; // eslint-disable-line eqeqeq
                                                return (
                                                    <label
                                                        key={id}
                                                        className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${checked
                                                            ? 'border-e2o-400 bg-e2o-50 dark:bg-e2o-950/30 dark:border-e2o-700 text-gray-800 dark:text-white'
                                                            : 'border-gray-200 dark:border-neutral-700 text-gray-700 dark:text-neutral-300 hover:bg-gray-50 dark:hover:bg-neutral-800'}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => toggleThematique(id)}
                                                            className="accent-e2o-500 shrink-0"
                                                        />
                                                        {logo && <img src={logo} alt="" className="w-6 h-6 rounded object-cover shrink-0" />}
                                                        <span className="min-w-0 truncate">{cl.nom_cycle_lang}</span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {roleConfig.showDemo && (isVisible('punchline') || isVisible('video') || isVisible('visuel_fixe')) && (
                                <div className="space-y-3">
                                    {/* En-tête */}
                                    <div className="flex items-center justify-between">
                                        <p className="text-xs font-semibold text-e2o-700 dark:text-e2o-400 uppercase tracking-wide">Votre webcast</p>
                                        {existingPrestaId && <span className="text-[10px] text-gray-300 dark:text-neutral-600">#{existingPrestaId}</span>}
                                    </div>

                                    {/* Punchline */}
                                    {DEMO_FIELDS.filter(f => isVisible(f.key)).map(({ key, label, required, type, maxLength }) => {
                                        const value = form[key] || '';
                                        const wordCount = value.trim() ? value.trim().split(/\s+/).length : 0;
                                        return (
                                            <div key={key}>
                                                <label className="block text-xs font-semibold text-gray-500 dark:text-neutral-400 mb-1">
                                                    {label}{required && <span className="text-red-400 ml-1">*</span>}
                                                </label>
                                                <input type={type} required={required} maxLength={maxLength} value={value} onChange={e => handleChange(key, e.target.value)}
                                                    className="w-full border border-gray-200 dark:border-neutral-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-e2o-400 focus:border-transparent" />
                                                <p className={`mt-1 text-right text-[11px] ${maxLength && value.length >= maxLength ? 'text-red-500' : 'text-gray-400 dark:text-neutral-500'}`}>
                                                    {wordCount} mot{wordCount > 1 ? 's' : ''}{maxLength ? ` · ${value.length}/${maxLength} caractères` : ''}
                                                </p>
                                            </div>
                                        );
                                    })}

                                    {/* Section vidéo — inputs | preview */}
                                    {isVisible('video') && (<>
                                        <p className="text-xs text-gray-500 dark:text-neutral-400">
                                            Vidéo diffusée en arrière-plan lors de votre webcast —{' '}
                                            <span className="font-semibold text-e2o-600 dark:text-e2o-400">recommandé</span>
                                        </p>
                                        <div className="rounded-xl border border-gray-100 dark:border-neutral-800 overflow-hidden">
                                            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100 dark:divide-neutral-800">
                                                {/* Gauche : inputs */}
                                                <div className="p-4 bg-white dark:bg-neutral-900 space-y-4">
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-600 dark:text-neutral-300 mb-1.5">
                                                            {HTMLReactParser(renumberOption(t('form.sections.PrestaIllustrationSection.video_illustration_option_2'), 1))}
                                                        </p>
                                                        <input type="file" accept=".mp4,.mov,video/mp4,video/quicktime" onChange={handleVideoHostedChange}
                                                            className="w-full text-sm text-gray-500 dark:text-neutral-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-medium file:bg-gray-100 dark:file:bg-neutral-800 file:text-gray-700 dark:file:text-neutral-200 hover:file:bg-gray-200 dark:hover:file:bg-neutral-700 cursor-pointer" />
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 h-px bg-gray-100 dark:bg-neutral-800" />
                                                        <span className="text-[11px] text-gray-400 dark:text-neutral-500">ou</span>
                                                        <div className="flex-1 h-px bg-gray-100 dark:bg-neutral-800" />
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold text-gray-600 dark:text-neutral-300 mb-1.5">
                                                            {HTMLReactParser(renumberOption(t('form.sections.PrestaIllustrationSection.video_illustration_option_1'), 2))}
                                                        </p>
                                                        <input type="url" value={form.video_url} onChange={e => handleChange('video_url', e.target.value)}
                                                            placeholder="https://www.youtube.com/watch?v=…"
                                                            className="w-full border border-gray-200 dark:border-neutral-700 rounded-lg px-3 py-2 text-sm bg-white dark:bg-neutral-800 text-gray-800 dark:text-white placeholder-gray-400 dark:placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-e2o-400 focus:border-transparent" />
                                                    </div>
                                                </div>
                                                {/* Droite : preview active */}
                                                <div className="flex items-center justify-center bg-neutral-950 dark:bg-neutral-950 min-h-[200px]">
                                                    {getYouTubeId(form.video_url) ? (
                                                        <div className="w-full aspect-video">
                                                            <iframe
                                                                src={`https://www.youtube.com/embed/${getYouTubeId(form.video_url)}?modestbranding=1&rel=0`}
                                                                className="w-full h-full"
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                                allowFullScreen
                                                                title="Aperçu YouTube"
                                                            />
                                                        </div>
                                                    ) : (videoHostedPreviewUrl || fetchedVideoHosted) ? (
                                                        <div className="w-full aspect-video">
                                                            <video
                                                                src={videoHostedPreviewUrl || `${MEDIA_BASE_URL}${fetchedVideoHosted}`}
                                                                className="w-full h-full object-contain"
                                                                controls
                                                                preload="metadata"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col items-center gap-2 text-neutral-600 p-6">
                                                            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                                                            <p className="text-xs">Aperçu vidéo</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>)}

                                    {/* Visuel fixe */}
                                    {isVisible('visuel_fixe') && (
                                        <div className="rounded-xl border border-orange-200 dark:border-orange-800/60 bg-orange-50 dark:bg-orange-900/20 p-4">
                                            <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                                                <p className="flex-1 min-w-0 text-sm text-orange-900 dark:text-orange-100 leading-relaxed">
                                                    Si je ne dispose pas de vidéo ci-dessus ; je télécharge un visuel fixe (.jpeg ou .png){' '}
                                                    <span className="font-semibold text-orange-700 dark:text-orange-300 italic">déconseillé</span>{' '}
                                                    et moins impactant qu'une vidéo qui illustrera mon webcast lors de ma prise de parole.
                                                </p>
                                                {/* Zone cliquable : aperçu 16:9 + libellé (input natif masqué) */}
                                                <label className="w-full sm:w-48 shrink-0 cursor-pointer group">
                                                    <input type="file" accept=".jpg,.jpeg,.png,image/jpeg,image/png" onChange={handlePrestaVisuelChange} className="sr-only" />
                                                    {prestaVisuelPreview
                                                        ? <img src={prestaVisuelPreview} alt="aperçu" className="w-full aspect-video object-cover rounded-lg border border-gray-200 dark:border-neutral-700 group-hover:opacity-90 transition-opacity" />
                                                        : (
                                                            <div className="w-full aspect-video rounded-lg border-2 border-dashed border-orange-300 dark:border-orange-800 bg-white/60 dark:bg-neutral-900/40 group-hover:border-orange-400 dark:group-hover:border-orange-600 transition-colors flex flex-col items-center justify-center gap-1.5 px-3 text-center">
                                                                <svg className="w-6 h-6 text-orange-400 dark:text-orange-500" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.16-5.16a2.25 2.25 0 013.18 0l5.16 5.16m-1.5-1.5l1.41-1.41a2.25 2.25 0 013.18 0l2.91 2.91M3.75 21h16.5A1.5 1.5 0 0021.75 19.5V4.5A1.5 1.5 0 0020.25 3H3.75A1.5 1.5 0 002.25 4.5v15A1.5 1.5 0 003.75 21zM13.5 8.25h.008v.008H13.5V8.25z" /></svg>
                                                                <span className="text-xs font-medium text-orange-700 dark:text-orange-300">Choisir une image</span>
                                                                <span className="text-[10px] text-orange-500/80 dark:text-orange-400/70">JPEG ou PNG</span>
                                                            </div>
                                                        )
                                                    }
                                                    {prestaVisuelPreview && (
                                                        <span className="block mt-1.5 text-center text-xs font-medium text-orange-700 dark:text-orange-300 group-hover:underline">Changer d'image</span>
                                                    )}
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {status === 'error' && (
                                <p className="text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">{errorMsg}</p>
                            )}

                            <button
                                type="submit"
                                disabled={status === 'submitting'}
                                className="w-full py-3 text-sm font-semibold text-white bg-e2o-500 hover:bg-e2o-600 rounded-lg transition-colors disabled:opacity-50 mt-2"
                            >
                                {status === 'submitting' ? 'Envoi en cours…' : t('form.envoyer')}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default SelfRegisterSlot;
